import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './InactivityLogout.css';
import Modal from '../Modal/Modal';
import ActionButtons from '../ActionButtons/ActionButtons';
import { CheckinContext } from '../../contexts/CheckinContext';
import logComponent from '../../common/logComponent';
import Loading from '../Loading/Loading';

export default function InactivityLogout({ maxInactivity, warningPeriod }) {
  const checkinContext = useContext(CheckinContext);
  const navigate = useNavigate();
  const actionButtons = useRef(null);
  const loadingRef = useRef(true);

  const [modalDisplayTimeout, setModalDisplayTimeout] = useState(null);
  const [addListenerTimeout, setAddListenerTimeout] = useState(null);
  const [timerUpdateInterval, setTimerUpdateInterval] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [logoutTime, setLogoutTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [modalShouldExit, setModalShouldExit] = useState(false);
  const [logout, setLogout] = useState(false);

  const logoutTimeRef = useRef(null);

  if (logComponent('InactivityLogout')) { console.debug('InactivityLogout rendered'); }

  useEffect(() => {
    if (logComponent('InactivityLogout')) { console.debug('InactivityLogout onMount'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setLogoutTimeThis();
    setAddListenerTimeoutThis();
    setLoadingRef(false);
    return function cleanup() {
      if (modalDisplayTimeout) clearTimeout(modalDisplayTimeout);
      if (addListenerTimeout) clearTimeout(addListenerTimeout);
      if (timerUpdateInterval) clearInterval(timerUpdateInterval);
    };
  }, [])

  useEffect(() => {
    if (logComponent('InactivityLogout')) { console.debug('InactivityLogout useEffect [logoutTime]'); }
    if (loadingRef.current) return;
    resetModalDisplayTimeout();
  }, [logoutTime])

  useEffect(() => {
    if (logComponent('InactivityLogout')) { console.debug('InactivityLogout useEffect  [checkinContext.checkinInProgress]'); }
    // If checkin has started, clear timeouts
    if (checkinContext.checkinInProgress) {
      clearTimeout(modalDisplayTimeout);
      clearTimeout(addListenerTimeout);
    }

    // If checkin has ended, create new timeouts
    if (!checkinContext.checkinInProgress) {
      setLogoutTimeThis();
      setAddListenerTimeoutThis();
    }
  }, [checkinContext.checkinInProgress])

  const setLoadingRef = (el) => {
    if (logComponent('InactivityLogout')) { console.debug('InactivityLogout setLoadingRef'); }
    loadingRef.current = el;
  }

  const setLogoutTimeRef = (el) => {
    if (logComponent('InactivityLogout')) { console.debug('InactivityLogout setLogoutTimeRef'); }
    logoutTimeRef.current = el;
    setLogoutTime(el);
  }

  const setLogoutTimeThis = () => {
    if (logComponent('InactivityLogout')) { console.debug('InactivityLogout setLogoutTimeThis'); }
    setLogoutTimeRef(Date.now() + (maxInactivity * 1000));
  }
  

  const addActivityListeners = () => {
    if (logComponent('InactivityLogout')) { console.debug('InactivityLogout addActivityListeners'); }
    document.addEventListener('keydown', handleActivity);
    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('touchmove', handleActivity);
  }

  const removeActivityListeners = () => {
    if (logComponent('InactivityLogout')) { console.debug('InactivityLogout removeActivityListeners'); }
    document.removeEventListener('keydown', handleActivity);
    document.removeEventListener('mousemove', handleActivity);
    document.removeEventListener('touchmove', handleActivity);
  }
  
  const handleActivity = () => {
    if (logComponent('InactivityLogout')) { console.debug('InactivityLogout handleActivity'); }
    removeActivityListeners();
    setAddListenerTimeoutThis();
    setLogoutTimeThis();
  }

  const resetModalDisplayTimeout = () => {
    if (logComponent('InactivityLogout')) { console.debug('InactivityLogout resetModalDisplayTimeout'); }

    const timeoutDelay = (maxInactivity - warningPeriod) * 1000; // both maxInactivity and warningPeriod are given in seconds

    clearTimeout(modalDisplayTimeout);
    setModalDisplayTimeout(setTimeout(showModal, timeoutDelay));
  }

  const setAddListenerTimeoutThis = () => {
    if (logComponent('InactivityLogout')) { console.debug('InactivityLogout setAddListenerTimeoutThis'); }
    setAddListenerTimeout(setTimeout(addActivityListeners, 60000));
  }

  const showModal = () => {
    if (logComponent('InactivityLogout')) { console.debug('InactivityLogout showModal'); }
    removeActivityListeners();

    setModalVisible(true);
    setModalShouldExit(false);
    setTimerUpdateInterval(setInterval(updateTimeLeft, 500));
  }

  const hideModal = () => {
    if (logComponent('InactivityLogout')) { console.debug('InactivityLogout hideModal'); }
    clearInterval(timerUpdateInterval);
    setModalVisible(false);
    setModalShouldExit(false);
    setTimerUpdateInterval(null);
    if (logout) navigate('/logout');
  }

  const updateTimeLeft = () => {
    if (logComponent('InactivityLogout')) { console.debug('InactivityLogout updateTimeLeft'); }

    if (logoutTime < Date.now()) {
      clearInterval(timerUpdateInterval);
      setLogout(true);
      startModalExit();
    } else {
      setTimeLeft(Math.ceil((logoutTimeRef.current - Date.now()) / 1000));
    }
  }

  const logoutThis = () => {
    if (logComponent('InactivityLogout')) { console.debug('InactivityLogout logoutThis'); }
    startModalExit();
    setLogout(true);
  }

  const stayLoggedIn = () => {
    if (logComponent('InactivityLogout')) { console.debug('InactivityLogout stayLoggedIn'); }
    handleActivity();
    startModalExit();
  }

  const startModalExit = () => {
    if (logComponent('InactivityLogout')) { console.debug('InactivityLogout startModalExit'); }
    setModalShouldExit(true);
  }

  return (
    modalVisible ?
      <Modal isVisible={true} remainOnBackdropClick={true} shouldExit={modalShouldExit} hide={hideModal}>
        <div className="Inactivity-Logout-Modal">
          <h3 className="Inactivity-Logout-Modal__heading">Inactivity Logout</h3>
          <p className="Inactivity-Logout-Modal__par">You have been inactive for a while. You are about to be automatically logged off.</p>
          <p className="Inactivity-Logout-Modal__par">If you wish to continue using MIMI, please click <span className="highlight">"Stay logged in"</span> below. If you're done, click <span className="highlight">"Log out"</span>.</p>
          <p className="Inactivity-Logout-Modal__par">Otherwise, you will be logged out in {timeLeft} second{timeLeft !== 1 ? 's' : ''}.</p>
          <ActionButtons
            ref={actionButtons}
            mainAction={stayLoggedIn}
            mainActionText="Stay logged in"
            warningAction={logoutThis}
            warningActionText="Log out"
          />
        </div>
      </Modal> :
      null
  );
}